import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { ManualEntryForm } from "./ManualEntryForm";
import { ResearchResults } from "./ResearchResults";
import { DickinsonCountyScraper } from "./DickinsonCountyScraper";
import { CsvImport } from "./CsvImport";
import { DickinsonTextParser } from "./DickinsonTextParser";
import { AllenCountyTextParser } from "./AllenCountyTextParser";
import { ChampaignCountyTextParser } from "./ChampaignCountyTextParser";
import { LickingCountyTextParser } from "./LickingCountyTextParser";
import { LorainCountyTextParser } from "./LorainCountyTextParser";
import { MedinaCountyTextParser } from "./MedinaCountyTextParser";
import { MercerCountyTextParser } from "./MercerCountyTextParser";
import { MuskingumCountyTextParser } from "./MuskingumCountyTextParser";
import { PortageCountyTextParser } from "./PortageCountyTextParser";
import { ComalCountyTextParser } from "./ComalCountyTextParser";
import { WashtenawCountyTextParser } from "./WashtenawCountyTextParser";
import { NewaygoCountyTextParser } from "./NewaygoCountyTextParser";
import { QuickCaptureMode } from "./QuickCaptureMode";
import { DirectScreenshotCapture } from "./DirectScreenshotCapture";
import { DataExportTool } from "./DataExportTool";
import type { SearchResults } from "./types";

const COUNTIES = [
  { value: 'allen', label: 'Allen County, OH' },
  { value: 'auglaize', label: 'Auglaize County, OH' },
  { value: 'bandera', label: 'Bandera County, TX' },
  { value: 'champaign', label: 'Champaign County, OH' },
  { value: 'comal', label: 'Comal County, TX' },
  { value: 'dickinson', label: 'Dickinson County, MI' },
  { value: 'guadalupe', label: 'Guadalupe County, TX' },
  { value: 'kendall', label: 'Kendall County, TX' },
  { value: 'kerr', label: 'Kerr County, TX' },
  { value: 'licking', label: 'Licking County, OH' },
  { value: 'lorain', label: 'Lorain County, OH' },
  { value: 'madison', label: 'Madison County, OH' },
  { value: 'medina', label: 'Medina County, OH' },
  { value: 'mercer', label: 'Mercer County, OH' },
  { value: 'muskingum', label: 'Muskingum County, OH' },
  { value: 'newaygo', label: 'Newaygo County, MI' },
  { value: 'noble', label: 'Noble County, OH' },
  { value: 'portage', label: 'Portage County, OH' },
  { value: 'richland', label: 'Richland County, OH' },
  { value: 'randall', label: 'Randall County, TX' },
  { value: 'washtenaw', label: 'Washtenaw County, MI' },
  { value: 'williamson', label: 'Williamson County, TX' },
  { value: 'travis', label: 'Travis County, TX' },
  { value: 'other', label: 'Other County' }
];

export function CountyResearchPage() {
  const [selectedCounty, setSelectedCounty] = useState('noble');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRecordSaved = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">County Research</h1>
        <p className="text-muted-foreground mt-1">
          Unified interface for county property research across all counties
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select County</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full max-w-xs">
            <Label htmlFor="county-select">Working County</Label>
            <Select value={selectedCounty} onValueChange={setSelectedCounty}>
              <SelectTrigger id="county-select">
                <SelectValue placeholder="Select a county" />
              </SelectTrigger>
              <SelectContent>
                {COUNTIES.map(county => (
                  <SelectItem key={county.value} value={county.value}>
                    {county.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="screenshot-bot" className="w-full">
        <TabsList>
          <TabsTrigger value="screenshot-bot">🤖 Screenshot Bot</TabsTrigger>
          <TabsTrigger value="quick-capture">⚡ Quick Capture</TabsTrigger>
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="automated">Automated Scraping</TabsTrigger>
          <TabsTrigger value="import">Import CSV</TabsTrigger>
          <TabsTrigger value="export">📊 Export Tool</TabsTrigger>
          <TabsTrigger value="results">View Records</TabsTrigger>
        </TabsList>

        <TabsContent value="screenshot-bot" className="space-y-4">
          <DirectScreenshotCapture 
            county={selectedCounty}
            onRecordSaved={handleRecordSaved}
          />
        </TabsContent>

        <TabsContent value="quick-capture" className="space-y-4">
          <QuickCaptureMode />
        </TabsContent>

        <TabsContent value="manual" className="space-y-4">
          <ManualEntryForm county={selectedCounty} onRecordSaved={handleRecordSaved} />
        </TabsContent>

        <TabsContent value="automated" className="space-y-4">
          {selectedCounty === 'dickinson' && (
            <DickinsonCountyScraper />
          )}
          {selectedCounty !== 'dickinson' && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-center">
                  Automated scraping configuration for {COUNTIES.find(c => c.value === selectedCounty)?.label} coming soon.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="import" className="space-y-4">
          {selectedCounty === 'dickinson' ? (
            <DickinsonTextParser onImportComplete={handleRecordSaved} />
          ) : selectedCounty === 'allen' ? (
            <AllenCountyTextParser onImportComplete={handleRecordSaved} />
          ) : selectedCounty === 'champaign' ? (
            <ChampaignCountyTextParser onImportComplete={handleRecordSaved} />
          ) : selectedCounty === 'comal' || selectedCounty === 'guadalupe' || selectedCounty === 'kendall' || selectedCounty === 'kerr' || selectedCounty === 'bandera' ? (
            <ComalCountyTextParser county={selectedCounty} onRecordsSaved={handleRecordSaved} />
          ) : selectedCounty === 'licking' ? (
            <LickingCountyTextParser onImportComplete={handleRecordSaved} />
          ) : selectedCounty === 'lorain' ? (
            <LorainCountyTextParser onImportComplete={handleRecordSaved} />
          ) : selectedCounty === 'medina' ? (
            <MedinaCountyTextParser onRecordsSaved={handleRecordSaved} />
          ) : selectedCounty === 'mercer' ? (
            <MercerCountyTextParser onRecordsSaved={handleRecordSaved} />
          ) : selectedCounty === 'muskingum' ? (
            <MuskingumCountyTextParser onRecordsSaved={handleRecordSaved} />
          ) : selectedCounty === 'portage' ? (
            <PortageCountyTextParser onRecordsSaved={handleRecordSaved} />
          ) : selectedCounty === 'washtenaw' ? (
            <WashtenawCountyTextParser />
          ) : selectedCounty === 'newaygo' ? (
            <NewaygoCountyTextParser onImportComplete={handleRecordSaved} />
          ) : (
            <CsvImport onImportComplete={handleRecordSaved} />
          )}
        </TabsContent>

        <TabsContent value="export" className="space-y-4">
          <DataExportTool />
        </TabsContent>

        <TabsContent value="results" className="space-y-4">
          <ResearchResults 
            searchResults={{
              records: [],
              isSearching: false,
              progress: 0,
              ownerFinanceCount: 0,
              addressesExtracted: 0
            }}
            onRefresh={handleRecordSaved}
            key={refreshTrigger}
            defaultCountyFilter={selectedCounty}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
